import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import axios from "axios";

// Load Stripe outside of the component to avoid recreating the Stripe object
const stripePromise = loadStripe("YOUR_PUBLISHABLE_KEY"); // Replace with your Stripe publishable key

// The inner form component that contains the card element and submission logic
const CheckoutForm = ({ quoteId }) => {
  const [email, setEmail] = useState("");
  const [amount, setAmount] = useState(0);
  // eslint-disable-next-line 
  const [productName, setProductName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [clientSecret, setClientSecret] = useState("");
  const [paymentIntentId, setPaymentIntentId] = useState("");
  const [quoteDetails, setQuoteDetails] = useState(null);

  const stripe = useStripe();
  const elements = useElements();

  // Fetch quote details and create payment intent
  useEffect(() => {
    const fetchQuoteDetails = async () => {
      try {
        const response = await axios.get(`/api/quotes/${quoteId}`);
        const quote = response.data.quote;

        setQuoteDetails(quote);
        // Set default email if available from quote
        if (quote.email) {
          setEmail(quote.email);
        }

        // Calculate amount based on quote details
        // This is a placeholder - adjust based on your actual pricing logic
        // For example, you might multiply quantity by a price per unit
        const calculatedAmount = quote.quantity * 100; // Example: 1 euro per unit
        setAmount(calculatedAmount);
        setProductName(`Commande #${quote.id}`);

        // Create a payment intent
        const paymentResponse = await axios.post(
          "/api/payments/create-intent",
          {
            amount: calculatedAmount,
            product_name: `Commande #${quote.id}`,
          }
        );

        setClientSecret(paymentResponse.data.clientSecret);
        setPaymentIntentId(paymentResponse.data.paymentIntentId);
      } catch (err) {
        setError(
          err.response?.data?.message ||
            "Une erreur est survenue lors du chargement des détails"
        );
      }
    };

    if (quoteId) {
      fetchQuoteDetails();
    }
  }, [quoteId]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet
      return;
    }

    // Validate email
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setError("Veuillez fournir une adresse email valide");
      setLoading(false);
      return;
    }

    // Confirm the payment with Stripe
    const result = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: elements.getElement(CardElement),
        billing_details: {
          email: email,
        },
      },
      receipt_email: email,
    });

    if (result.error) {
      setError(`Erreur de paiement: ${result.error.message}`);
      setLoading(false);
    } else {
      if (result.paymentIntent.status === "succeeded") {
        // Notify your backend about the successful payment
        try {
          await axios.post("/api/payments/confirm", {
            payment_intent_id: paymentIntentId,
            quote_id: quoteId,
            email: email,
          });

          setSuccess(true);
          // Update quote status if needed
          await axios.post(`/api/quotes/${quoteId}/payment-success`, {
            email: email,
          });
        } catch (err) {
          console.error("Error confirming payment on server:", err);
          // Payment was successful, but our server notification failed
          // We should still show success to the user
          setSuccess(true);
        }
      }
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="text-center">
          <svg
            className="mx-auto h-12 w-12 text-green-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M5 13l4 4L19 7"
            ></path>
          </svg>
          <h2 className="mt-4 text-2xl font-bold text-gray-900">
            Paiement réussi !
          </h2>
          <p className="mt-2 text-gray-600">
            Merci pour votre commande. Un reçu a été envoyé à {email}.
          </p>
          <div className="mt-6">
            <a href="/" className="text-blue-600 hover:text-blue-700">
              Retour à l'accueil
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {quoteDetails && (
        <div className="bg-gray-50 p-4 rounded-md">
          <h3 className="text-lg font-medium text-gray-900">
            Détails de la commande
          </h3>
          <div className="mt-2 space-y-2">
            <p className="text-sm text-gray-700">
              Produit: {quoteDetails?.product_name || "Produit personnalisé"}
            </p>
            <p className="text-sm text-gray-700">
              Quantité: {quoteDetails?.quantity}
            </p>
            <p className="text-sm text-gray-700">
              Total: {(amount / 100).toFixed(2)} $
            </p>
          </div>
        </div>
      )}

      <div>
        <label
          htmlFor="email"
          className="block text-sm font-medium text-gray-700"
        >
          Adresse e-mail
        </label>
        <div className="mt-1">
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>
      </div>

      <div>
        <label
          htmlFor="card"
          className="block text-sm font-medium text-gray-700"
        >
          Informations de carte
        </label>
        <div className="mt-1">
          <CardElement
            id="card"
            options={{
              style: {
                base: {
                  fontSize: "16px",
                  color: "#424770",
                  "::placeholder": {
                    color: "#aab7c4",
                  },
                },
                invalid: {
                  color: "#9e2146",
                },
              },
            }}
            className="p-3 border border-gray-300 rounded-md shadow-sm"
          />
        </div>
      </div>

      <div>
        <button
          type="submit"
          disabled={!stripe || loading}
          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {loading
            ? "Traitement en cours..."
            : `Payer ${(amount / 100).toFixed(2)} $`}
        </button>
      </div>
    </form>
  );
};

// The outer component that sets up Stripe Elements
const CheckoutFormContainer = () => {
  const { id } = useParams();

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Paiement de votre commande
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <Elements stripe={stripePromise}>
            <CheckoutForm quoteId={id} />
          </Elements>
        </div>
      </div>
    </div>
  );
};

export default CheckoutFormContainer;
